import React from "react";
import ReactDOM from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import { i18nReady } from "@/i18n";
import "@/index.css";
import App from "@/App";

// Set app identity on root element for CSS variable theming
document.documentElement.setAttribute("data-app", "snugtale");

// Native status bar: dark icons on the app's white background.
// (Style.Light = light background → dark content/icons in Capacitor's naming.)
// Lazy-loaded so web builds don't pull the native plugin bridge.
if (Capacitor.isNativePlatform()) {
  import("@capacitor/status-bar")
    .then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: "#FFFFFF" }).catch(() => {});
    })
    .catch(() => {
      /* plugin unavailable — ignore */
    });
}

// Android 15+ edge-to-edge: pull cached system-bar insets from the native shell
// and write them into CSS vars so `body` padding respects the status/nav bars.
// No-op in browsers (AndroidInsets is undefined).
function applyAndroidInsets() {
  const bridge = (window as any).AndroidInsets;
  if (!bridge?.getInsets) return;
  try {
    const i = JSON.parse(bridge.getInsets()) as {
      top: number; bottom: number; left: number; right: number;
    };
    const el = document.documentElement.style;
    el.setProperty("--sai-top", `${i.top}px`);
    el.setProperty("--sai-bottom", `${i.bottom}px`);
    el.setProperty("--sai-left", `${i.left}px`);
    el.setProperty("--sai-right", `${i.right}px`);
  } catch { /* bridge not ready; the native listener will push values shortly */ }
}
applyAndroidInsets();
window.addEventListener("resize", applyAndroidInsets);
window.addEventListener("orientationchange", applyAndroidInsets);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
// The detected language's bundle is loaded before the first render (German is
// bundled, so this is a microtask; other languages wait for one ~10 KB chunk
// instead of rendering a spinner first). See src/i18n/index.ts.
i18nReady.then(() => {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
});
